import { useState } from "react";
import { observer } from "mobx-react-lite";
import { ChevronDown } from "lucide-react";

import { cn } from "@/lib/utils";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { ProjectPickerPanel } from "@/components/building/ProjectPickerPanel";
import { useCreateProjectDialog } from "@/domains/projects/components/createProjectDialogContext";
import { useLanguage } from "@/lib/i18n/LanguageContext";
import { useBuildingScope } from "@/hooks/buildings/useBuildingScope";
import { pageInfoTarget } from "@/components/page-info/pageInfoTarget";

interface ProjectScopeSwitcherProps {
  buildingId: string;
  className?: string;
}

/**
 * The project switcher in the building context line — a chevron sitting
 * against the project crumb that opens the searchable project list.
 *
 * Only mounted next to a resolved project crumb, which BuildingLayout renders
 * in Projects view alone (Building view is whole-building scoped and has no
 * project to switch). The crumb's own label stays a link to that project's
 * dashboard: the name says where you are and goes there, the chevron changes
 * where you are — the same split the sidebar's view-mode pill uses between its
 * two halves.
 *
 * Switching is a pure scope change (`useBuildingScope.setProject` → `?project=`
 * plus the per-session scope store), so the current page stays put and re-reads
 * itself against the new project. The sidebar toggle keeps its own dropdown for
 * the other job — *entering* Projects view, which needs a project picked before
 * the mode flips.
 */
export const ProjectScopeSwitcher = observer(function ProjectScopeSwitcher({
  buildingId,
  className,
}: ProjectScopeSwitcherProps) {
  const { t } = useLanguage();
  const {
    selectedProjectId,
    setProject,
    projectOptions,
    optionsLoaded,
    optionsError,
    reloadOptions,
  } = useBuildingScope(buildingId);
  const { openCreateProject, canCreateProjects } = useCreateProjectDialog();
  const [open, setOpen] = useState(false);

  const handleSelect = (projectId: number) => {
    setOpen(false);
    // Re-picking the project already in scope is handed to setProject like any
    // other pick: it drops `folderId` (back to the project root) and writes
    // nothing when the params come out unchanged. Skipping the call here would
    // skip that cleanup too.
    setProject(projectId);
  };

  // Creating from the switcher scopes straight to the new project — that's
  // what the picker was opened for, and the provider does it with the layout's
  // scope hook rather than this component's. The dialog outlives the switcher:
  // the crumb carrying it goes as soon as the view flips to Building or the
  // route is the headerless /model, and a `setProject` from an unmounted
  // instance writes against the location that instance last saw. The shared
  // dialog also lives outside this popover, which closes first, so the two
  // never nest.
  const handleCreateProject = () => {
    setOpen(false);
    openCreateProject();
  };

  return (
    <Popover open={open} onOpenChange={setOpen} modal={true}>
      <PopoverTrigger asChild>
        <button
          {...pageInfoTarget("layout.projectSwitcher")}
          type="button"
          aria-label={t.buildings.scope.switchProject}
          aria-haspopup="dialog"
          aria-expanded={open}
          className={cn(
            "flex h-5 w-5 shrink-0 items-center justify-center rounded text-muted-foreground",
            "transition-colors hover:text-foreground",
            "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1",
            className
          )}
        >
          <ChevronDown className="h-4 w-4" aria-hidden="true" />
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-[280px] p-0">
        <ProjectPickerPanel
          projects={projectOptions}
          selectedProjectId={selectedProjectId}
          optionsLoaded={optionsLoaded}
          optionsError={optionsError}
          onReload={reloadOptions}
          onSelect={handleSelect}
          onCreateProject={canCreateProjects ? handleCreateProject : undefined}
        />
      </PopoverContent>
    </Popover>
  );
});
